import i18next from 'i18next';
import { find, transform } from 'lodash';

import { FormikSelectOption, Money } from 'components';
import { AssetType, UserRole } from 'enums';
import { Asset, Shop } from 'types';

import { formatUtils } from './format.utils';

const getAssetOptions = (
  assets?: Asset[],
  role?: UserRole,
): FormikSelectOption[] =>
  transform(
    assets || [],
    (acc, asset) => {
      const allowedTypes = [AssetType.Balance, AssetType.External];
      if (role === UserRole.Admin) {
        allowedTypes.push(AssetType.InsuranceDeposit);
      }
      if (allowedTypes.includes(asset.type)) {
        acc.push({
          label: (
            <div>
              {asset.shop?.name && (
                <div>{formatUtils.formatName(asset.shop)}</div>
              )}
              {asset.p2pProvider?.name && (
                <div>{formatUtils.formatName(asset.p2pProvider)}</div>
              )}
              <Money
                key={asset.id}
                assetCurrencyId={asset.assetCurrencyId}
                value={asset.balance}
                symbol
              />
              {role === UserRole.Admin &&
                asset.user?.role === UserRole.Trader && (
                  <span>{` - ${i18next.t(`assets.type.${asset.type}`)}`}</span>
                )}
            </div>
          ),
          value: asset.id,
          disabled: !asset.id,
        });
      }
    },
    [] as FormikSelectOption[],
  );

const filterAssets = (
  assets: Asset[] = [],
  filters: { userId?: string; assetCurrencyId?: string } = {},
  shops: Shop[] = [],
) =>
  transform(
    assets,
    (acc, asset) => {
      if (filters.userId && asset.userId !== filters.userId) {
        return;
      }
      if (
        filters.assetCurrencyId &&
        asset.assetCurrencyId !== filters.assetCurrencyId
      ) {
        return;
      }

      acc.push(mergeAssetWithShop(asset, shops));
    },
    [] as Asset[],
  );

const mergeAssetWithShop = (asset: Asset, shops: Shop[] = []) => {
  const shop = find<Shop>(shops, (shop) => shop.id === asset.shopId);
  if (shop) {
    asset.shop = shop;
  }
  return asset;
};

const getShopAssetsWithDefaults = (
  assets: Asset[],
  shops: Shop[],
  userId: string,
  defaultAssetCurrencyId: string,
): Asset[] => {
  const shopIdsWithAsset = new Set(
    assets.filter((a) => a.shopId).map((a) => a.shopId),
  );

  return shops
    .filter((shop) => shop.userId === userId && !shopIdsWithAsset.has(shop.id))
    .map(
      (shop) =>
        ({
          id: shop.id,
          shopId: shop.id,
          shop: { name: shop.name, webSiteUrl: shop.webSiteUrl },
          userId,
          type: AssetType.Balance,
          balance: 0,
          holdBalance: 0,
          assetCurrencyId: defaultAssetCurrencyId,
        } as Asset),
    );
};

export const assetUtils = {
  getAssetOptions,
  filterAssets,
  getShopAssetsWithDefaults,
};
